import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'

function MaesterReportUpload({ clientId }) {
  const { user } = useAuth()
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  const fetchReports = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('maester_reports')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError
      setReports(data || [])
    } catch (err) {
      console.error('[MaesterReportUpload] Fetch error:', err)
    } finally {
      setLoading(false)
    }
  }, [clientId])

  useEffect(() => {
    fetchReports()
  }, [fetchReports])

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate HTML file
    if (!file.name.endsWith('.html') && !file.name.endsWith('.htm')) {
      setError('Please select an HTML file (.html or .htm)')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be under 10MB')
      return
    }

    setUploading(true)
    setError(null)

    try {
      const timestamp = Date.now()
      const filePath = `${clientId}/${timestamp}-${file.name}`

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('maester-reports')
        .upload(filePath, file, {
          contentType: 'text/html',
          upsert: false
        })

      if (uploadError) throw uploadError

      // Create database record
      const { error: dbError } = await supabase
        .from('maester_reports')
        .insert({
          client_id: clientId,
          file_path: filePath,
          file_name: file.name,
          uploaded_by: user.id
        })

      if (dbError) throw dbError

      await fetchReports()
    } catch (err) {
      console.error('[MaesterReportUpload] Upload error:', err)
      setError(err.message || 'Upload failed. Please try again.')
    } finally {
      setUploading(false)
      // Reset file input
      e.target.value = ''
    }
  }

  const handleDelete = async (report) => {
    try {
      // Delete from storage
      await supabase.storage
        .from('maester-reports')
        .remove([report.file_path])

      // Delete database record
      const { error: dbError } = await supabase
        .from('maester_reports')
        .delete()
        .eq('id', report.id)

      if (dbError) throw dbError

      setReports(prev => prev.filter(r => r.id !== report.id))
    } catch (err) {
      console.error('[MaesterReportUpload] Delete error:', err)
      setError(err.message || 'Delete failed.')
    }
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">M365 Security Reports</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Upload Maester HTML reports for client viewing
          </p>
        </div>
        <label className={`btn-primary text-sm cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          {uploading ? 'Uploading...' : 'Upload Report'}
          <input
            type="file"
            accept=".html,.htm"
            onChange={handleUpload}
            disabled={uploading}
            className="hidden"
          />
        </label>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center py-8">
          <svg className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <p className="text-gray-500 dark:text-gray-400 mb-1">No reports uploaded yet</p>
          <p className="text-xs text-gray-400 dark:text-gray-500">Upload a Maester HTML report to share with the client.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {reports.map(report => (
            <div
              key={report.id}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{report.file_name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(report.created_at).toLocaleDateString()} at {new Date(report.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleDelete(report)}
                className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors flex-shrink-0"
                title="Delete report"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default MaesterReportUpload
